import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Activity, Sparkles, Zap, ChevronRight, Infinity as InfinityIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { sandboxApi } from '../services/api';

// Brand accent for the credit system — spec says #00ff88.
const ACCENT = '#00FF88';
const ACCENT_DIM = 'rgba(0,255,136,0.15)';
const ACCENT_LINE = 'rgba(0,255,136,0.35)';

const EARN_ROWS = [
  { key: 'daily_login', label: 'Daily sign-in', value: '+10 min' },
  { key: 'streak_3', label: '3-day streak', value: '+20 min' },
  { key: 'room_complete', label: 'Complete a room', value: '+30 min' },
  { key: 'first_hint_daily', label: 'First AI hint today', value: '+5 min' },
  { key: 'referral_accepted', label: 'Referral accepted', value: '+15 min' },
  { key: 'streak_7', label: '7-day streak', value: '+45 min' },
];

const ProState = ({ navigate }) => (
  <Card
    data-testid="sandbox-credits-card-pro"
    className="bg-[#0F1A12] border-[1px]"
    style={{ borderColor: ACCENT_LINE }}
  >
    <CardHeader className="pb-3">
      <CardTitle className="text-white text-sm flex items-center gap-2">
        <Activity className="w-4 h-4" style={{ color: ACCENT }} />
        Sandbox time
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex items-center gap-2 mb-1">
        <InfinityIcon className="w-5 h-5" style={{ color: ACCENT }} />
        <span className="text-2xl font-bold text-white">Unlimited</span>
      </div>
      <p className="text-xs text-gray-400">
        Pro plan — terminal and analysis run as long as you need.
      </p>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/profile')}
        className="mt-3 text-xs text-gray-400 hover:text-white px-0"
      >
        View activity <ChevronRight className="w-3 h-3 ml-1" />
      </Button>
    </CardContent>
  </Card>
);

const ProgressBar = ({ balance, base, totalCap, earned, earnedCap }) => {
  // Two-stack bar: base segment + earned segment. Each shown as a proportion of totalCap.
  const basePct = Math.min(100, (Math.min(balance, base) / totalCap) * 100);
  const earnedPct = Math.min(100, (Math.max(0, balance - base) / totalCap) * 100);
  const earnRingPct = Math.min(100, (earned / earnedCap) * 100);

  return (
    <div className="space-y-2">
      <div
        data-testid="credits-progress-bar"
        className="h-3 rounded-full bg-[#0A1410] overflow-hidden flex"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={totalCap}
        aria-valuenow={balance}
      >
        <div
          style={{ width: `${basePct}%`, backgroundColor: ACCENT }}
          className="h-full"
        />
        <div
          style={{
            width: `${earnedPct}%`,
            background: `repeating-linear-gradient(45deg, ${ACCENT}, ${ACCENT} 4px, rgba(0,255,136,0.55) 4px, rgba(0,255,136,0.55) 8px)`,
          }}
          className="h-full"
        />
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-500 font-mono">
        <span>0 min</span>
        <span data-testid="credits-earned-meter">
          Earned: {earned}/{earnedCap}{' '}
          <span className="inline-block w-12 h-1 align-middle ml-1 rounded-full bg-[#0A1410] overflow-hidden">
            <span
              className="block h-full"
              style={{ width: `${earnRingPct}%`, backgroundColor: ACCENT }}
            />
          </span>
        </span>
        <span>{totalCap} max</span>
      </div>
    </div>
  );
};

const FreeState = ({ state, navigate }) => {
  const { balance, base, earned, earnedCap, totalCap, lowCreditNudge } = state;

  return (
    <Card
      data-testid="sandbox-credits-card"
      className="bg-[#0F1A12] border-[1px]"
      style={{ borderColor: ACCENT_LINE }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Activity className="w-4 h-4" style={{ color: ACCENT }} />
            Sandbox time
          </CardTitle>
          <div
            className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
            style={{ backgroundColor: ACCENT_DIM, color: ACCENT }}
          >
            Free plan
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Headline balance — framed as earned, not remaining */}
        <div>
          <div className="flex items-baseline gap-2">
            <span
              data-testid="credits-balance"
              className="text-3xl font-bold tabular-nums"
              style={{ color: ACCENT }}
            >
              {balance}
            </span>
            <span className="text-sm text-gray-400">/ {totalCap} min</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            <span className="text-white font-medium">{base}</span> base &middot;{' '}
            <span style={{ color: ACCENT }} className="font-medium">+{earned}</span> earned this month
          </p>
        </div>

        <ProgressBar
          balance={balance}
          base={base}
          totalCap={totalCap}
          earned={earned}
          earnedCap={earnedCap}
        />

        {/* Soft contextual nudge — only at low balance, never modal */}
        {lowCreditNudge && (
          <div
            data-testid="credits-low-nudge"
            className="rounded-md px-3 py-2 text-xs flex items-start gap-2"
            style={{ backgroundColor: ACCENT_DIM, color: '#D6FFE8' }}
          >
            <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
            <span>
              Running low — finish today&apos;s daily challenge to add {' '}
              <span className="font-semibold" style={{ color: ACCENT }}>+30 min</span>.
            </span>
          </div>
        )}

        {/* Earn opportunities — frame as XP catalog */}
        <details className="group" data-testid="credits-earn-catalog">
          <summary className="cursor-pointer text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 select-none">
            <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
            How to earn more
          </summary>
          <div className="mt-2 space-y-1.5 pl-4 border-l" style={{ borderColor: ACCENT_LINE }}>
            {EARN_ROWS.map((r) => (
              <div
                key={r.key}
                className="flex items-center justify-between text-[11px] text-gray-400"
              >
                <span>{r.label}</span>
                <span className="font-mono" style={{ color: ACCENT }}>{r.value}</span>
              </div>
            ))}
          </div>
        </details>

        <Button
          data-testid="credits-upgrade-btn"
          onClick={() => navigate('/pricing')}
          variant="outline"
          size="sm"
          className="w-full border-gray-700 text-gray-300 hover:bg-[#00FF88]/10 hover:text-white hover:border-[#00FF88]/40"
        >
          <Zap className="w-3 h-3 mr-1.5" style={{ color: ACCENT }} />
          Unlock unlimited time with Pro
        </Button>
      </CardContent>
    </Card>
  );
};

const SkeletonState = () => (
  <Card
    data-testid="sandbox-credits-skeleton"
    className="bg-[#0F1A12] border-[1px] animate-pulse"
    style={{ borderColor: ACCENT_LINE }}
  >
    <CardContent className="py-6">
      <div className="h-3 w-24 bg-gray-800 rounded mb-3" />
      <div className="h-8 w-32 bg-gray-800 rounded mb-3" />
      <div className="h-3 w-full bg-gray-800 rounded" />
    </CardContent>
  </Card>
);

/**
 * Sandbox-credits dashboard widget.
 * Pro users see the unlimited variant; Free users see the XP-style meter
 * with earn-catalog and soft low-credit nudge.
 */
const SandboxCreditsCard = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await sandboxApi.getCredits();
        if (!cancelled) setState(s);
      } catch (err) {
        // Unauthenticated or transient — hide gracefully.
        console.debug('[sandbox-credits] load failed:', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <SkeletonState />;
  if (!state) return null;
  if (state.isPro) return <ProState navigate={navigate} />;
  return <FreeState state={state} navigate={navigate} />;
};

export default SandboxCreditsCard;
